import { V2Button } from '@abyss/web/ui/Button';
import { V2CoachmarkTour } from '@abyss/web/ui/Coachmark';
import { Markdown } from '@abyss/web/ui/Markdown';
import React, { useEffect, useState } from 'react';

type GuestCoachmarkProps = {
  title: string;
  content: string;
  onClose: () => void;
  onLearnMore?: () => void;
  learnMoreButtonText?: string;
  showLearnMoreButton?: boolean;
  target: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
};

const CoachmarkStyling = {
  'abyss-coachmark-root': {
    width: '329px',
    minWidth: '250px',
    maxWidth: '560px',
    minHeight: '159px',
    height: 'auto',
    padding: '24px',
    zIndex: 9999,
    borderRadius: '16px',
    backgroundColor:
      'var(--web-semantic-color-surface-container-inverse, #4B4D4F)',
    color: 'var(--web-semantic-color-text-content-primary-alt, #FFFFFF)',
    boxShadow: '0px 6px 8px -2px #00000029',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

    '& p': {
      margin: 0,
      fontSize: '12px',
      lineHeight: '140%',
      fontWeight: 400,
      letterSpacing: '0',
      color: 'var(--web-semantic-color-text-content-primary-alt, #FFFFFF)',
    },
    '& p + p': {
      marginTop: '2px',
    },

    '@media (max-width: 767px)': {
      width: '100%',
      maxWidth: 'calc(100vw - 32px)',
    },
  },
  'abyss-coachmark-title': {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: '0',
    color: 'var(--web-semantic-color-text-content-primary-alt, #FFFFFF)',
    display: 'block',
    marginBottom: '2px',
  },
  'abyss-coachmark-content-container': {
    display: 'none',
  },
  'abyss-coachmark-skip-button': {
    position: 'absolute' as const,
    top: '5.42px',
    right: '5.42px',
    width: '13.15px',
    height: '13.15px',
  },
  'abyss-coachmark-pointer': {
    backgroundColor:
      'var(--web-semantic-color-surface-container-inverse, #4B4D4F)',
  },
};

const GuestCoachmark: React.FC<GuestCoachmarkProps> = ({
  title,
  content,
  onClose,
  onLearnMore,
  learnMoreButtonText = '',
  showLearnMoreButton = true,
  target,
  position = 'right',
}) => {
  const [coachmarkPosition, setCoachmarkPosition] = useState<
    'top' | 'bottom' | 'left' | 'right'
  >(position);
  const [spacerHeight, setSpacerHeight] = useState<number>(0);
  const isMobile = coachmarkPosition === 'bottom';

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCoachmarkPosition('bottom');
      } else {
        setCoachmarkPosition(position);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position]);

  useEffect(() => {
    if (isMobile) {
      let calculatedHeight: number = 140;

      if (showLearnMoreButton && onLearnMore && learnMoreButtonText) {
        calculatedHeight += 10;
      }

      setSpacerHeight(calculatedHeight);
    } else {
      setSpacerHeight(0);
    }
  }, [isMobile, showLearnMoreButton, onLearnMore, learnMoreButtonText]);

  const extraContent = (
    <div
      style={{
        width: '297px',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginTop: '-12px',
      }}
    >
      <div
        style={{
          fontSize: '12px',
          lineHeight: '140%',
          fontWeight: 400,
          letterSpacing: '0',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          color: 'var(--web-semantic-color-text-content-primary-alt, #FFFFFF)',
        }}
      >
        <Markdown>{content}</Markdown>
      </div>
      {showLearnMoreButton && onLearnMore && learnMoreButtonText && (
        <V2Button
          css={{
            width: '104px',
            height: '32px',
            minHeight: '32px',
            borderRadius: '20px',
            backgroundColor:
              'var(--button-color-surface-container-secondary-rest, #FFFFFF)',
            color: 'var(--button-color-text-label-outline-brand, #002677)',
            border:
              '1px solid var(--button-color-border-container-secondary, #002677)',
            padding: '8px 24px',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: '140%',
            letterSpacing: '0',
            textAlign: 'center' as const,
            whiteSpace: 'nowrap' as const,
            alignSelf: 'flex-start',
            '& span': {
              fontWeight: 700,
              fontSize: '12px',
              lineHeight: '140%',
              letterSpacing: '0',
              whiteSpace: 'nowrap',
              color: 'var(--button-color-text-label-outline-brand, #002677)',
            },
            '&:hover': {
              backgroundColor: '#f0f0f0',
              color: 'var(--button-color-text-label-outline-brand, #002677)',
              border:
                '1px solid var(--button-color-border-container-secondary, #002677)',
            },
          }}
          data-testid="learn-more-button-guest-coachmark"
          onClick={onLearnMore}
          variant="filled"
        >
          {learnMoreButtonText}
        </V2Button>
      )}
    </div>
  );

  return (
    <>
      <V2CoachmarkTour
        css={CoachmarkStyling}
        data-testid="guest-coachmark-container"
        onFinish={onClose}
        onSkip={onClose}
        startTour={true}
        steps={[
          {
            target,
            title,
            description: '',
            extraContent,
            position: coachmarkPosition,
          },
        ]}
        variant="gray"
      >
        <></>
      </V2CoachmarkTour>
      {isMobile && spacerHeight > 0 && (
        <div
          aria-hidden="true"
          style={{
            height: `${spacerHeight}px`,
            width: '100%',
            pointerEvents: 'none',
          }}
        />
      )}
    </>
  );
};

export default GuestCoachmark;
